import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import {
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  signOut,
} from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import * as ROUTES from "../constants/routes";
import { useAuth } from "../services/use-auth";
import Button from "../components/Button";
import AuthLayout from "./AuthLayout";
import { FormCard, AuthTitle, AuthSubtitle, TextLink, ErrorText } from "./authStyles";

const SetNewPasswordComponent = () => {
  const authHook = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorFeedback, setErrorFeedback] = useState(null);
  const [processing, setProcessing] = useState(false);
  // Tracks whether the Auth password was already changed this session. If the
  // subsequent flag-clear fails, a retry must NOT re-run reauth/updatePassword
  // because the temporary password no longer works.
  const [passwordChanged, setPasswordChanged] = useState(false);

  const passwordTooShort = newPassword.length > 0 && newPassword.length < 8;
  const passwordsMismatch =
    confirmPassword.length > 0 && newPassword !== confirmPassword;
  const invalid =
    currentPassword === "" ||
    newPassword.length < 8 ||
    newPassword !== confirmPassword;

  const submit = async () => {
    if (invalid) return;
    setProcessing(true);
    setErrorFeedback(null);
    try {
      const currentUser = authHook.auth.currentUser;
      if (!passwordChanged) {
        const credential = EmailAuthProvider.credential(
          currentUser.email,
          currentPassword,
        );
        await reauthenticateWithCredential(currentUser, credential);
        await updatePassword(currentUser, newPassword);
        setPasswordChanged(true);
      }
      await updateDoc(doc(authHook.db, "users", currentUser.uid), {
        mustChangePassword: false,
      });
      // Suppress the LoadingGuard redirect for the next render so the freshly
      // changed user isn't bounced back here before userData reflects the flag.
      sessionStorage.setItem("passwordJustChanged", "1");
      navigate(ROUTES.REPORTS);
    } catch (error) {
      if (
        error.code === "auth/wrong-password" ||
        error.code === "auth/invalid-credential"
      ) {
        setErrorFeedback("Current/temporary password is incorrect.");
      } else if (error.code === "auth/weak-password") {
        setErrorFeedback("Password is too weak.");
      } else {
        setErrorFeedback(error.message || "Failed to change password.");
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleSignOut = async () => {
    try {
      sessionStorage.setItem("justLoggedOut", "true");
      await signOut(authHook.auth);
    } finally {
      navigate(ROUTES.SIGN_IN);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !invalid && !processing) {
      event.preventDefault();
      submit();
    }
  };

  const fieldColor = theme.palette.mode === "light" ? "secondary" : "primary";

  return (
    <AuthLayout>
      <FormCard>
        <AuthTitle theme={theme}>Set a New Password</AuthTitle>
        <AuthSubtitle theme={theme}>
          An administrator set a temporary password for your account. Choose a
          new password to continue.
        </AuthSubtitle>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            pb: 3,
            width: "100%",
          }}
          noValidate
          autoComplete="off"
          onKeyDown={handleKeyDown}>
          <TextField
            fullWidth
            label="Current / Temporary Password"
            type="password"
            name="currentPassword"
            variant="outlined"
            color={fieldColor}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <TextField
            fullWidth
            label="New Password"
            type="password"
            name="newPassword"
            variant="outlined"
            color={fieldColor}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={passwordTooShort}
            helperText={
              passwordTooShort ? "Must be at least 8 characters" : null
            }
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            name="confirmPassword"
            variant="outlined"
            color={fieldColor}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={passwordsMismatch}
            helperText={passwordsMismatch ? "Passwords do not match" : null}
          />
        </Box>
        <Button
          disabled={invalid}
          variant="contained"
          onClick={submit}
          processing={processing}>
          Update Password
        </Button>
        <ErrorText>{errorFeedback}</ErrorText>
        <TextLink theme={theme} onClick={handleSignOut}>
          Sign out instead
        </TextLink>
      </FormCard>
    </AuthLayout>
  );
};

const SetNewPasswordPage = (props) => {
  if (props.stillLoading) return null;
  return <SetNewPasswordComponent />;
};

export default SetNewPasswordPage;
